import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import bookingChangesApi from '@/features/dashboard/bookingChanges/api/bookingChangesApi.ts';
import { bookingChangesApiQueryKeys } from '@/features/dashboard/bookingChanges/api/bookingChangesApi.queryKeys.ts';
import { ResolveBookingChangeRequest } from '@/features/dashboard/bookingChanges/types/dto/ResolveBookingChangeRequest.ts';
import { ChangeResolution } from '@/features/dashboard/bookingChanges/types/dto/ChangeResolution.ts';
import { bookingsApiQueryKeys } from '@/features/dashboard/bookings/api/bookingsApi.queryKeys.ts';
import { participantsApiQueryKeys } from '@/features/dashboard/participants/api/participantsApi.queryKeys.ts';
import { clientsApiQueryKeys } from '@/features/dashboard/clients/api/clientsApi.queryKeys.ts';
import { setCachedOpenDraft } from '@/features/dashboard/bookingChanges/hooks/useGetOpenDraft.ts';
import { invalidateTourStatsQueries } from '@/utils/invalidateTourStatsQueries.ts';

interface ResolveDraftParams {
  changeId: string;
  request: ResolveBookingChangeRequest;
}

/**
 * Wprowadzenie paczki (krok W2): od razu albo do akceptacji klienta.
 *
 * Backend odrzuci żądanie, gdy przy niezerowej dopłacie zabraknie
 * `correctionDueDate` lub `correctionPaymentMethods`, a przy trasie z aneksem
 * — pustej listy `amendmentTourConsentIds`.
 */
const useResolveDraft = (bookingId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ changeId, request }: ResolveDraftParams) =>
      bookingChangesApi.resolveDraft(bookingId, changeId, request),
    onSuccess: (change, { request }) => {
      // Świeży status idzie do pamięci podręcznej przed unieważnieniem, bo
      // podglądy paczki pytają cache o to, czy wciąż jest otwarta — a lista
      // paczek odświeży się dopiero za chwilę.
      setCachedOpenDraft(bookingId, change);
      // Wprowadzona paczka przepisuje księgę, dane uczestników i zapisującego oraz
      // harmonogram rat, więc sam prefiks paczek nie wystarczy — bez tego operator
      // ogląda po zastosowaniu wartości sprzed zmiany aż do przeładowania strony.
      // Uczestników i zapisujących unieważniamy od korzenia: paczka rusza dowolną
      // osobę z rezerwacji, a ich identyfikatorów tu nie znamy.
      [
        bookingChangesApiQueryKeys.booking(bookingId),
        bookingsApiQueryKeys.byId(bookingId),
        participantsApiQueryKeys.all(),
        clientsApiQueryKeys.all(),
      ].forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
      invalidateTourStatsQueries(queryClient);
      toast.success(
        request.resolution === ChangeResolution.APPLY_NOW
          ? 'Paczka zmian została wprowadzona'
          : 'Paczka zmian została wysłana do akceptacji',
      );
    },
    onError: (error: Error) => {
      toast.error('Nie udało się wprowadzić paczki zmian', {
        description: error.message,
      });
    },
  });
};

export default useResolveDraft;
